import { useState, useEffect, useRef, useCallback } from 'react';
import { diff_match_patch } from "diff-match-patch";
import { md5 } from "../Utils/hash";

export const useCollaborativeEditing = ({
    projectId,
    fileId,
    initialContent,
    onContentUpdate,
    toast,
    enabled = true
}) => {
    const [isConnected, setIsConnected] = useState(false);
    const [activeUsers, setActiveUsers] = useState([]);
    const [otherUsersCursors, setOtherUsersCursors] = useState({});
    let userCounter = useRef(1);
    let userColorMap = useRef({});
    const getUserColor = (email) => {
        if (!userColorMap.current[email]) {
            const colors = ["#E91E63", "#2196F3", "#4CAF50", "#FF9800", "#9C27B0", "#00BCD4", "#FF5722"];
            let hash = 0;
            for (let i = 0; i < email.length; i++) {
                hash = email.charCodeAt(i) + ((hash << 5) - hash);
            }
            userColorMap.current[email] = colors[Math.abs(hash) % colors.length];
        }
        return userColorMap.current[email];
    };
    function getCursorFromPatch(patchText, shadowText) {
        try {
            const dmp = new diff_match_patch();
            const patches = dmp.patch_fromText(patchText);
            if (!patches || patches.length === 0) return null;

            const lastPatch = patches[patches.length - 1];
            let contextLength = 0;
            for (const diff of lastPatch.diffs) {
                const [op, text] = diff;
                if (op === 0) {
                    contextLength += text.length;
                } else {
                    // First non-equal diff = where change starts
                    break;
                }
            }
            const charIndex = lastPatch.start1 + contextLength;
            // Convert char index → row/col
            const textBefore = shadowText.substring(0, charIndex);
            const lines = textBefore.split('\n');
            const row = lines.length - 1;
            const col = lines[lines.length - 1].length;

            return { row, col, charIndex };
        } catch (e) {
            console.error("Error extracting cursor:", e);
            return null;
        }
    }
    useEffect(() => {
        const interval = setInterval(() => {
            const now = Date.now();
            setOtherUsersCursors(prev => {
                const updated = { ...prev };
                Object.keys(updated).forEach(userId => {
                    if (now - updated[userId].timestamp > 5000) {
                        delete updated[userId];  // remove stale cursor
                    }
                });
                return updated;
            });
        }, 2000);
        return () => clearInterval(interval);
    }, []);
    const ws = useRef(null);
    const dmp = useRef(new diff_match_patch()).current;
    // Shadow State
    const shadowText = useRef(initialContent || "");
    const shadowHash = useRef("");
    // Initialize shadow on file load/change
    useEffect(() => {
        if (initialContent) {
            shadowText.current = initialContent;
            shadowHash.current = md5(initialContent);
        }
    }, [initialContent, fileId]); // Reset when fileId changes or initial content loads

    useEffect(() => {
        if (enabled && projectId && fileId) {
            const serverUrl = window.global.server_url || "";
            const wsProtocol = serverUrl.startsWith("https") ? "wss://" : "ws://";
            const host = serverUrl.replace(/^https?:\/\//, "");
            const wsUrl = `${wsProtocol}${host}ws/collab/${projectId}/${fileId}/`;

            console.log("Connecting to WebSocket:", wsUrl);
            const sanitizeForCss = (str) => {
                return str.replace(/[^a-zA-Z0-9_-]/g, '_');
            };


            try {
                ws.current = new WebSocket(wsUrl);

                ws.current.onopen = () => {
                    console.log("WebSocket Connected state:", ws.current.readyState);
                    setIsConnected(true);
                    // toast({
                    //     title: "WebSocket Connected",
                    //     status: "success",
                    //     duration: 2000,
                    //     isClosable: true,
                    // });
                    // Send an initial ping or handshake if needed
                    ws.current.send(JSON.stringify({ type: "ping" }));
                };

                ws.current.onmessage = (event) => {
                    try {
                        console.log("WS Receive:", event.data);
                        const data = JSON.parse(event.data);

                        switch (data.type) {

                            case 'file_update':
                                const patches = dmp.patch_fromText(data.patch);
                                const [newContent, results] = dmp.patch_apply(patches, shadowText.current);
                                if (results.every(r => r)) {
                                    const cursor = getCursorFromPatch(data.patch, shadowText.current);
                                    console.log("the current user name", data.user?.name, "email", data.user?.email);
                                    // Use email as unique key, fallback for anonymous
                                    const userEmail = data.user?.email || null;
                                    const userName = data.user?.name?.trim() || null;
                                    const displayName = userName || userEmail || "Anonymous";
                                    const uniqueKey = sanitizeForCss(
                                        (userEmail || `anon_${Math.random().toString(36).substr(2, 9)}`)
                                    );

                                    const color = userEmail
                                        ? getUserColor(userEmail)           // stable color by email
                                        : "#9E9E9E";                        // fixed grey for anonymous

                                    if (cursor) {
                                        setOtherUsersCursors(prev => ({
                                            ...prev,
                                            [uniqueKey]: {
                                                row: cursor.row,
                                                col: cursor.col,
                                                color: color,
                                                label: displayName,
                                                timestamp: Date.now()
                                            }
                                        }));
                                    }

                                    shadowText.current = newContent;
                                    shadowHash.current = data.hash;

                                    if (onContentUpdate) {
                                        onContentUpdate(newContent);
                                    }
                                } else {
                                    console.error("Patch application failed locally.");
                                }
                                break;
                            case 'ack':
                                console.log("Ack received, hash:", data.hash);
                                shadowHash.current = data.hash;
                                break;

                            case 'error':
                                console.error("Sync Error:", data.message);
                                toast({
                                    title: "Sync Error",
                                    description: data.message,
                                    status: "error",
                                    duration: 4000
                                });
                                if (data.server_content) {
                                    shadowText.current = data.server_content;
                                    shadowHash.current = data.server_hash || md5(data.server_content);
                                    if (onContentUpdate) {
                                        onContentUpdate(data.server_content);
                                    }
                                }
                                break;

                            case 'active_users':
                                setActiveUsers(data.users || []);
                                break;

                            case 'user_joined':
                                if (data.user) {
                                    setActiveUsers(prev => [...prev.filter(u => u.email !== data.user.email), data.user]);
                                    const joinName = data.user.name?.trim() || data.user.email || "Anonymous";
                                    toast({
                                        title: `${joinName} joined`,
                                        status: "info",
                                        duration: 2000,
                                        position: "bottom-right"
                                    });
                                }
                                break;

                            case 'user_left':
                                if (data.user_id) {
                                    setActiveUsers(prev => prev.filter(u => u.id !== data.user_id));
                                }
                                break;

                            default:
                                break;
                        }
                    } catch (e) {
                        console.error("Error processing WS message", e);
                    }
                };

                ws.current.onerror = (error) => {
                    console.error("WebSocket Error:", error);
                    setIsConnected(false);
                    // toast({
                    //     title: "WebSocket Error",
                    //     description: "Connection error",
                    //     status: "error",
                    //     duration: 3000
                    // });
                };

                ws.current.onclose = () => {
                    console.log("WebSocket Disconnected");
                    setIsConnected(false);
                };

            } catch (e) {
                console.error("WebSocket connection failed", e);
                setIsConnected(false);
            }

            return () => {
                if (ws.current) {
                    ws.current.close();
                }
            };
        } else {
            if (ws.current) {
                ws.current.close();
                ws.current = null;
            }
            setIsConnected(false);
        }
    }, [enabled, projectId, fileId, toast, dmp]); // Added dependencies

    // Heartbeat to keep connection alive
    useEffect(() => {
        let heartbeat;
        if (isConnected && ws.current) {
            heartbeat = setInterval(() => {
                if (ws.current.readyState === WebSocket.OPEN) {
                    console.log("WS Heartbeat: ping");
                    ws.current.send(JSON.stringify({ type: "ping" }));
                }
            }, 30000); // 30s heartbeat
        }
        return () => clearInterval(heartbeat);
    }, [isConnected]);

    const timerRef = useRef(null);
    const pendingContentRef = useRef(null);

    const sendEdit = useCallback((newContent) => {
        console.log("sendEdit called with length:", newContent?.length);
        pendingContentRef.current = newContent;

        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        timerRef.current = setTimeout(() => {
            const content = pendingContentRef.current;
            if (!content) return; // Should not happen if set correctly

            if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
                return;
            }

            try {
                const patch = dmp.patch_make(shadowText.current, content);
                const patchText = dmp.patch_toText(patch);

                // Don't send empty patches
                if (patchText) {
                    const payload = JSON.stringify({
                        type: 'patch',
                        patch: patchText,
                        hash: shadowHash.current
                    });
                    console.log("WS Send:", payload);
                    ws.current.send(payload);
                    // Optimistically update local shadow
                    shadowText.current = content;
                }
            } catch (e) {
                console.error("Error sending edit:", e);
            }
            // Clear pending
            pendingContentRef.current = null;
        }, 500); // 500ms debounce

        return true;
    }, [dmp]);

    return {
        isConnected,
        activeUsers,
        sendEdit,
        otherUsersCursors
    };
};
